import { OptJobType } from 'api/optJobs/optJobs.api'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  CollectedSettingsFilterMode,
  getOptimizationTypeOptions,
  getPrioritiesFromPriorityLevels,
  type OptimizationSubtypeOption,
} from '../DigitalTwinCollectedSettingsView.helper'

type UseCollectedSettingsFilterParams = {
  isCollectedSettings: boolean
  priorities: SystemSettingPriorityLevel[]
  baseSettingPriorities?: SystemSettingPriorityLevel[]
  subtypesByOptJobType: Partial<Record<OptJobType, Subtype[]>> | undefined
  translations?: { [key: string]: string }
  t: (key: string) => string
}

type SwitchItem = {
  value: CollectedSettingsFilterMode
  label: string
}

type UseCollectedSettingsFilterResult = {
  collectedSettingsFilterMode: CollectedSettingsFilterMode
  setCollectedSettingsFilterMode: (mode: CollectedSettingsFilterMode) => void
  availablePriorityLevels: number[]
  selectedPriorityLevels: number[]
  setSelectedPriorityLevels: (levels: number[]) => void
  togglePriorityLevel: (level: number) => void
  optimizationSubtypeOptions: ReturnType<typeof getOptimizationTypeOptions>
  selectedOptimizationSubtypeId: string | null
  setSelectedOptimizationSubtypeId: (id: string) => void
  collectedSettingsFilterSwitchItems: SwitchItem[]
  effectiveSelectedPriorityLevels: number[]
  appliedPriorities: SystemSettingPriorityLevel[]
}

const VALID_COLLECTED_SETTINGS_FILTER_MODES: CollectedSettingsFilterMode[] = ['deviation', 'optimization']

function getInitialCollectedSettingsFilterMode(): CollectedSettingsFilterMode {
  try {
    const stored = localStorage.getItem('collectedSettingsFilterMode')

    if (stored && VALID_COLLECTED_SETTINGS_FILTER_MODES.includes(stored as CollectedSettingsFilterMode)) {
      return stored as CollectedSettingsFilterMode
    }
  } catch {
    return 'deviation'
  }

  return 'deviation'
}

function getInitialSelectedOptimizationSubtypeId(): string | null {
  try {
    return localStorage.getItem('collectedSettingsSelectedOptimizationSubtypeId')
  } catch {
    return null
  }
}

function getDefaultOptimizationTypeOptionId(options: OptimizationSubtypeOption[]): string {
  return (options.find((option) => !option.isSplitIntoSubtypes) ?? options[0]).id
}

function getFirstSubtypeOptionId(options: OptimizationSubtypeOption[], splitOption: OptimizationSubtypeOption): string {
  return (
    options.find((option) => option.optJobType === splitOption.optJobType && !option.isSplitIntoSubtypes)?.id ??
    getDefaultOptimizationTypeOptionId(options)
  )
}

/**
 * A split opt job type stays selectable by hand, but is never restored from storage or picked as the
 * default: it has no applied settings, so a user would otherwise land on the disabled card.
 */
function resolveSelectedOptimizationTypeOptionId(
  options: OptimizationSubtypeOption[],
  selectedId: string | null,
  isRestoredFromStorage: boolean
): string {
  const selectedOption = options.find((option) => option.id === selectedId)

  if (!selectedOption) {
    return getDefaultOptimizationTypeOptionId(options)
  }

  if (isRestoredFromStorage && selectedOption.isSplitIntoSubtypes) {
    return getFirstSubtypeOptionId(options, selectedOption)
  }

  return selectedOption.id
}

export function useCollectedSettingsFilter({
  isCollectedSettings,
  priorities,
  baseSettingPriorities,
  subtypesByOptJobType,
  translations,
  t,
}: UseCollectedSettingsFilterParams): UseCollectedSettingsFilterResult {
  const [inactivePriorityLevels, setInactivePriorityLevels] = useState<number[]>([])

  const [collectedSettingsFilterMode, setCollectedSettingsFilterMode] = useState<CollectedSettingsFilterMode>(
    getInitialCollectedSettingsFilterMode
  )

  const optimizationSubtypeOptions = useMemo(
    () => getOptimizationTypeOptions(subtypesByOptJobType, priorities, translations),
    [subtypesByOptJobType, priorities, translations]
  )

  const [selectedOptimizationSubtypeId, setSelectedOptimizationSubtypeId] = useState<string | null>(
    getInitialSelectedOptimizationSubtypeId
  )
  const previousSelectedOptimizationSubtypeId = useRef<string | null>(null)
  const isSelectionRestoredFromStorage = useRef(true)

  const collectedSettingsFilterSwitchItems = useMemo<SwitchItem[]>(
    () => [
      { value: 'deviation', label: t('Deviation type') },
      { value: 'optimization', label: t('Optimization type') },
    ],
    [t]
  )

  useEffect(() => {
    try {
      localStorage.setItem('collectedSettingsFilterMode', collectedSettingsFilterMode)
    } catch {
      // Ignore storage write errors (for example restricted browser storage mode).
    }
  }, [collectedSettingsFilterMode])

  useEffect(() => {
    if (!selectedOptimizationSubtypeId) {
      return
    }

    try {
      localStorage.setItem('collectedSettingsSelectedOptimizationSubtypeId', selectedOptimizationSubtypeId)
    } catch {
      // Ignore storage write errors (for example restricted browser storage mode).
    }
  }, [selectedOptimizationSubtypeId])

  useEffect(() => {
    if (optimizationSubtypeOptions.length === 0) {
      setSelectedOptimizationSubtypeId(null)
      return
    }

    const resolvedId = resolveSelectedOptimizationTypeOptionId(
      optimizationSubtypeOptions,
      selectedOptimizationSubtypeId,
      isSelectionRestoredFromStorage.current
    )
    isSelectionRestoredFromStorage.current = false

    if (resolvedId !== selectedOptimizationSubtypeId) {
      setSelectedOptimizationSubtypeId(resolvedId)
    }
  }, [optimizationSubtypeOptions, selectedOptimizationSubtypeId])

  useEffect(() => {
    if (previousSelectedOptimizationSubtypeId.current === selectedOptimizationSubtypeId) {
      return
    }

    previousSelectedOptimizationSubtypeId.current = selectedOptimizationSubtypeId
    setInactivePriorityLevels([])
  }, [selectedOptimizationSubtypeId])

  const selectedOptimizationPriorityLevels = useMemo(() => {
    if (!selectedOptimizationSubtypeId) {
      return []
    }

    return (
      optimizationSubtypeOptions.find((option) => option.id === selectedOptimizationSubtypeId)?.priorityLevels ?? []
    )
  }, [optimizationSubtypeOptions, selectedOptimizationSubtypeId])

  const availablePriorityLevels = useMemo(() => {
    if (isCollectedSettings && collectedSettingsFilterMode === 'optimization') {
      const basePriorityLevels = (baseSettingPriorities ?? []).map((priority) => priority.priority_level)
      return [...new Set([...basePriorityLevels, ...selectedOptimizationPriorityLevels])]
    }

    return priorities.map((priority) => priority.priority_level)
  }, [
    baseSettingPriorities,
    collectedSettingsFilterMode,
    isCollectedSettings,
    priorities,
    selectedOptimizationPriorityLevels,
  ])

  const selectedPriorityLevels = useMemo(
    () => availablePriorityLevels.filter((level) => !inactivePriorityLevels.includes(level)),
    [availablePriorityLevels, inactivePriorityLevels]
  )

  const setSelectedPriorityLevels = useCallback(
    (levels: number[]) => {
      const selectedLevelSet = new Set(levels)

      setInactivePriorityLevels((prevInactiveLevels) => {
        const inactiveOutsideAvailable = prevInactiveLevels.filter((level) => !availablePriorityLevels.includes(level))
        const inactiveInAvailable = availablePriorityLevels.filter((level) => !selectedLevelSet.has(level))
        return [...new Set([...inactiveOutsideAvailable, ...inactiveInAvailable])]
      })
    },
    [availablePriorityLevels]
  )

  const togglePriorityLevel = useCallback(
    (level: number) => {
      if (!availablePriorityLevels.includes(level)) {
        return
      }

      if (selectedPriorityLevels.includes(level)) {
        setSelectedPriorityLevels(selectedPriorityLevels.filter((selectedLevel) => selectedLevel !== level))
        return
      }

      setSelectedPriorityLevels([...selectedPriorityLevels, level])
    },
    [availablePriorityLevels, selectedPriorityLevels, setSelectedPriorityLevels]
  )

  const effectiveSelectedPriorityLevels = useMemo(() => {
    if (!isCollectedSettings) {
      return priorities.map((priority) => priority.priority_level)
    }

    return selectedPriorityLevels
  }, [isCollectedSettings, priorities, selectedPriorityLevels])

  const appliedPriorities = useMemo(() => {
    if (isCollectedSettings) {
      return getPrioritiesFromPriorityLevels(effectiveSelectedPriorityLevels, [
        ...(baseSettingPriorities ?? []),
        ...priorities,
      ])
    }

    return priorities
  }, [baseSettingPriorities, effectiveSelectedPriorityLevels, isCollectedSettings, priorities])

  return {
    collectedSettingsFilterMode,
    setCollectedSettingsFilterMode,
    availablePriorityLevels,
    selectedPriorityLevels,
    setSelectedPriorityLevels,
    togglePriorityLevel,
    optimizationSubtypeOptions,
    selectedOptimizationSubtypeId,
    setSelectedOptimizationSubtypeId,
    collectedSettingsFilterSwitchItems,
    effectiveSelectedPriorityLevels,
    appliedPriorities,
  }
}
